import { Scrollable } from "components/Scrollable/Scrollable"
import { Spinner } from "components/Spinner/Spinner"
import { Role } from "interface/user"
import { ComponentProps, useEffect, useMemo } from "react"
import { useInfiniteChatMessages } from "services/communications"
import { cn } from "styles/cn"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { getChatMessageDisplayTime, isToday } from "utils/date"
import { ChatItems } from "./ChatItems"
import { useChat } from "./ChatProvider"
import { groupMessages } from "./utils"

export function ChatList({
	senderRoles = ["Provider", "Field-support-agent"],
	...props
}: ComponentProps<"div"> & { senderRoles?: Role[] }) {
	const {
		sender,
		activeParticipant,
		patient,
		threadId,
		noPriorComms,
		setNoPriorComms,
	} = useChat()
	const {
		data: { pages: messages = [] } = {},
		hasNextPage,
		fetchNextPage,
		isFetching,
		isFetchingNextPage,
	} = useInfiniteChatMessages(
		patient.id,
		activeParticipant.id !== patient.id ? activeParticipant.id : undefined,
		threadId,
	)
	const messageGroupByTimeBySender = useMemo(
		() => groupMessages(messages),
		[messages],
	)
	const loading = isFetching || isFetchingNextPage

	useEffect(() => {
		setNoPriorComms(!isFetching && messages.length === 0)
	}, [isFetching, messages.length, setNoPriorComms])

	return (
		<Scrollable.ReverseVertical
			{...props}
			role="feed"
			size={messages.length}
			className={cn("px-3", props.className)}
			aria-label={`${loading ? "Loading " : ""}${activeParticipant.fullName}'s message thread`}
			scroll={{
				hasNextPage,
				loading,
				onLoadMore: fetchNextPage,
			}}
		>
			{!hasNextPage && !loading && !noPriorComms && (
				<p className="p-2 text-center">No more messages</p>
			)}

			<Spinner spinning={loading}>
				<div className="flex flex-col gap-1 py-4">
					{messageGroupByTimeBySender.map(
						({ time, messages: groupedMessages }, index) => {
							const previous = messageGroupByTimeBySender[index - 1]
							const isSameDay = previous
								? previous.time.isSame(time, "day") && isToday(time)
								: false
							const messageTimestamp = getChatMessageDisplayTime(
								time,
								!isSameDay,
							)
							const ariaLabel = `${groupedMessages.length} message${groupedMessages.length > 1 ? "s" : ""} starting ${messageTimestamp}`

							return (
								<article
									key={messageTimestamp}
									role="region"
									aria-label={ariaLabel}
								>
									<div className="py-2 text-center text-sm text-navy-60">
										{messageTimestamp}
									</div>
									{groupedMessages.map((group) => {
										const senderName = (() => {
											if (group.sender === activeParticipant.id) {
												return activeParticipant.fullName
											} else if (group.sender === sender.id) {
												return sender.fullName
											}
											return group.senderInfo?.fullName ?? DEFAULT_PLACEHOLDER
										})()
										const alignRight = senderRoles.includes(
											group.senderInfo.userType!,
										)

										return (
											<ChatItems
												key={`chat-list-${group.messages[0].id}}`}
												messages={group.messages}
												senderId={group.sender}
												senderName={senderName}
												alignRight={alignRight}
											/>
										)
									})}
								</article>
							)
						},
					)}
				</div>
			</Spinner>
		</Scrollable.ReverseVertical>
	)
}
